import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import DateCurrentInput from "../inputs/DateCurrentInput";
import { SubprocessItemRow } from "./OSSubprocessItemsTable";
import { parseDecimalInput } from "../utils/decimalInput";
import RNMaterialPriemVerifyCell from "./RNMaterialPriemVerifyCell";
import RNRazmerCellSaveMark from "./RNRazmerCellSaveMark";
import type { RNMaterialPriemRowData } from "./RNMaterialPriemRows";

interface Props {
  row: RNMaterialPriemRowData;
  isSaving?: boolean;
  currentUserId?: string;
  onPatchActual: (patch: Partial<SubprocessItemRow>) => void;
  onPatchActualAndSave: (patch: Partial<SubprocessItemRow>) => void;
  isSaveQueued?: boolean;
}

const cellCls = "border border-gray-300 h-[26px] px-1.5 py-0.5 text-[11px] text-gray-700 break-words";
const actualInputCls = "w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50";

const formatDate = (value?: string): string => {
  if (!value) return "";
  const date = moment(value);
  return date.isValid() ? date.format("DD.MM.YYYY") : value;
};

const RNMaterialPriemRow: React.FC<Props> = ({ row, isSaving, currentUserId, onPatchActual, onPatchActualAndSave, isSaveQueued }) => {
  const { t } = useTranslation();
  const qty = parseDecimalInput(row.quantity);
  const allowedYield = parseDecimalInput(row.allowedYield);
  const measured = parseDecimalInput(row.measuredQty);
  const isOverage = measured != null && qty != null && measured > qty;
  const isShortfall = measured != null && allowedYield != null && measured < allowedYield;
  const hasDeviation = isOverage || isShortfall;
  const yieldPct = measured != null && qty ? `${((measured / qty) * 100).toFixed(1)}%` : "";
  const fromTo = row.fromPct || row.toPct ? `${row.fromPct || "-"} / ${row.toPct || "-"}` : "";

  return (
    <tr className="hover:bg-blue-50 focus-within:bg-blue-50">
      <td className={`${cellCls} text-center`}>{row.priority}</td>
      <td className={`${cellCls} text-center`}>{row.subprocessName}</td>
      <td className={cellCls}>{row.ident}</td>
      <td className={cellCls} title={row.name}>{row.name}</td>
      <td className={`${cellCls} text-center`}>{row.unitCode}</td>
      <td className={`${cellCls} text-right tabular-nums`}>{row.quantity}</td>
      <td className={`${cellCls} text-right tabular-nums bg-gray-50`}>{row.issuedQty}</td>
      <td className={`${cellCls} text-right tabular-nums`}>{row.secondaryQty}</td>
      <td className={`${cellCls} text-right tabular-nums`}>{row.allowedWaste ? `${row.allowedWaste}%` : ""}</td>
      <td className={`${cellCls} text-right tabular-nums`}>{row.allowedYield}</td>
      <td className={`${cellCls} text-center`}>{fromTo}</td>
      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
        <input
          type="text"
          inputMode="decimal"
          className={`${actualInputCls} text-right tabular-nums`}
          value={row.measuredQty || ""}
          disabled={isSaving}
          onChange={(event) => onPatchActual({ measuredQty: event.target.value, confirmed: false, verifiedByName: "", verifiedAt: "", verifyRequested: false })}
        />
      </td>
      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
        <input
          type="text"
          inputMode="decimal"
          className={`${actualInputCls} text-right tabular-nums`}
          value={row.uzurkuvanje || ""}
          disabled={isSaving}
          onChange={(event) => onPatchActual({
            uzurkuvanje: event.target.value,
            verifiedBy: undefined,
            verifiedByName: "",
            verifiedAt: "",
            verifyRequested: false,
          })}
        />
      </td>
      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
        <input
          className={actualInputCls}
          value={row.serialNumber || ""}
          disabled={isSaving}
          onChange={(event) => onPatchActual({ serialNumber: event.target.value })}
        />
      </td>
      <td className="border border-gray-300 h-[26px] px-1 py-0.5">
        <DateCurrentInput
          clearable={false}
          value={row.expiryDate || ""}
          onChange={(value) => onPatchActual({ expiryDate: value })}
          disabled={isSaving}
          inputClassName={actualInputCls}
        />
      </td>
      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
        <input
          type="text"
          inputMode="decimal"
          className={`${actualInputCls} text-right tabular-nums`}
          value={row.rastur || ""}
          disabled={isSaving}
          onChange={(event) => onPatchActual({ rastur: event.target.value })}
        />
      </td>
      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
        <input
          type="text"
          inputMode="decimal"
          className={`${actualInputCls} text-right tabular-nums`}
          value={row.waste || ""}
          disabled={isSaving}
          onChange={(event) => onPatchActual({ waste: event.target.value })}
        />
      </td>
      <td className={`${cellCls} text-right tabular-nums`}>{yieldPct}</td>
      <td className={`${cellCls} text-center`}>
        {hasDeviation && (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-800"
            title={isOverage ? t('os.materialno.priemOverageTitle') || '' : t('os.materialno.priemShortfallTitle') || ''}
          >
            <AlertTriangle size={10} />
            {isOverage ? t('os.materialno.priemOverage') : t('os.materialno.priemShortfall')}
          </span>
        )}
      </td>
      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
        <input
          className={`${actualInputCls} ${hasDeviation && !(row.deviationReason || '').trim() ? 'ring-1 ring-red-400' : ''}`}
          value={row.deviationReason || ""}
          disabled={isSaving}
          placeholder={hasDeviation ? (t('os.materialno.priemJustificationRequired') || '') : ''}
          onChange={(event) => onPatchActual({ deviationReason: event.target.value })}
        />
      </td>
      <td className={`${cellCls} text-center`}>{row.documentTypeCode}</td>
      <td className={`${cellCls} text-center`}>{row.documentNumber}</td>
      <td className="relative border border-gray-300 h-[26px] px-1 py-0.5 text-center">
        <input
          type="checkbox"
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          checked={!!row.confirmed}
          disabled={!(row.measuredQty || "").trim()}
          title={t('os.materialno.priemConfirmRow') || ''}
          onChange={(event) => onPatchActualAndSave(event.target.checked
            ? { confirmed: true }
            : { confirmed: false, verifiedByName: "", verifiedAt: "", verifyRequested: false })}
        />
        <RNRazmerCellSaveMark status={isSaving || isSaveQueued ? "saving" : undefined} />
      </td>
      <td className={`${cellCls} bg-gray-50`}>
        {[row.workedByName, formatDate(row.workedAt)].filter(Boolean).join(" / ")}
      </td>
      <RNMaterialPriemVerifyCell
        row={row}
        currentUserId={currentUserId}
        isSaving={isSaving}
        isSaveQueued={isSaveQueued}
        onVerifyRequested={(checked) => onPatchActualAndSave({ verifyRequested: checked })}
      />
    </tr>
  );
};

export default RNMaterialPriemRow;
